/**
 * FileUploadSection Component
 * 
 * Handles file upload UI with drag-and-drop support.
 * Displays selected files and upload button.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import DragDropFileUpload from '@/features/data-upload/components/DragDropFileUpload';
import SubmitButton from '@/shared/components/buttons/SubmitButton';

export interface FileUploadSectionProps {
  /** Currently selected files */
  selectedFiles: File[];
  /** Callback when files are selected */
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Callback to remove a file at index */
  onRemoveFile: (index: number) => void;
  /** Callback to clear all files */
  onClearAll: () => void;
  /** Callback to trigger upload */
  onUpload: () => void;
  /** Whether upload is in progress */
  isUploading: boolean;
  /** Ref for file input element */
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}

export const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  selectedFiles,
  onFileSelect,
  onRemoveFile,
  onClearAll,
  onUpload,
  isUploading,
  fileInputRef,
}) => {
  const { t } = useTranslation('dataAdjustments');

  return (
    <div className="mb-4 sm:mb-8">
      <h2 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-4">
        {t('fileInput.label')}
      </h2>
      
      <DragDropFileUpload
        onFileChange={onFileSelect}
        fileInputRef={fileInputRef}
        accept=".csv"
        multiple={true}
        selectedFiles={selectedFiles}
        onRemoveFile={onRemoveFile}
        onClearAll={onClearAll}
      />

      {/* Upload Button */}
      {selectedFiles.length > 0 && (
        <div className="mt-4">
          <SubmitButton
            onClick={onUpload}
            disabled={isUploading || selectedFiles.length === 0}
          >
            {isUploading
              ? t('processing')
              : t('selectedFiles.uploadFiles')}
          </SubmitButton>
        </div>
      )}
    </div>
  );
};

export default FileUploadSection;
